import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { flatMap, share } from 'rxjs/operators';

import { DigihavenEnvironmentConfig } from '../entities/config/environment-config.entity';
import { AUTOLOGIN } from '../injection-tokens/autologin';
import { ConfigService } from '../services/config/config.service';
import { UserTokens } from './../entities/user/user-tokens.entity';

@Injectable()
export class DigihavenAuthenticationService {
    private readonly CLIENT_ID: string;
    private readonly CLIENT_SECRET: string;

    private readonly loginUrl: string;
    private readonly refreshUrl: string;

    get oauthLogoutUrl(): string {
        const url: string = `${this.configService.config.authorizationUrl}auth/?logout`;
        if (this.configService.config.logoutRedirectUrl) {
            const redirectUri: string = encodeURIComponent(this.configService.config.logoutRedirectUrl);
            return `${url}&redirect_uri=${redirectUri}`;
        }
        return url;
    }

    constructor(
        private readonly http: HttpClient,
        private readonly configService: ConfigService<DigihavenEnvironmentConfig>,
        @Inject(AUTOLOGIN) private autologin: 'digitaalarchief' | 'serieregister' | 'depotbeheer'
    ) {
        this.loginUrl = this.configService.config.authenticationUrl;
        this.refreshUrl = `${this.configService.config.authorizationUrl}auth/oauth2/token`;

        this.CLIENT_ID = this.configService.config.clientId;
        this.CLIENT_SECRET = this.configService.config.clientSecret;
    }

    getOauthUrl(newStateKey: string | null): string {
        const url: string = this.configService.config.authorizationUrl;
        const params = new HttpParams({
            fromObject: {
                client_id: this.CLIENT_ID,
                response_type: 'code',
                autologin: this.autologin,
                force_autologin: 'true',
                ...(newStateKey && { state: newStateKey })
            }
        });
        return `${url}auth/?${params}`;
    }

    login(username: string, password: string): Observable<UserTokens> {
        const cd: FormData = new FormData();

        cd.append('client_id', this.CLIENT_ID);
        cd.append('client_secret', this.CLIENT_SECRET);
        cd.append('username', username);
        cd.append('password', password);

        return this.http
            .post<UserTokens>(this.loginUrl, cd)
            .pipe(flatMap(tokens => this.refreshToken(tokens.refresh_token)));
    }

    requestAccessToken(token: string): Observable<UserTokens> {
        const credentials: object = {
            grant_type: 'authorization_code',
            code: token,
            client_id: this.CLIENT_ID,
            client_secret: this.CLIENT_SECRET
        };

        const headers: HttpHeaders = new HttpHeaders();
        headers.append('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.post<UserTokens>(this.refreshUrl, credentials, { headers });
    }

    refreshToken(refreshToken: string): Observable<UserTokens> {
        const credentials: object = {
            client_id: this.CLIENT_ID,
            client_secret: this.CLIENT_SECRET,
            grant_type: 'refresh_token',
            refresh_token: refreshToken
        };

        const headers: HttpHeaders = new HttpHeaders();
        headers.append('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.post<UserTokens>(this.refreshUrl, credentials, { headers }).pipe(share());
    }
}
